import { Fragment } from 'react'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { Box } from '@mui/material'
import { Attribute, AttributeLabel, AttributeValue } from 'packages/eid-ui'
import { useAccessRequestPolicy, useGroup } from 'hooks'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@mui/material'
import { ResponsibleParty } from 'components'
import useSubcomponents from 'useSubcomponents'
import { ResourceOwnersList } from 'components/Owners/ResourceOwnersList'
import { ResourceApproversList } from 'components/Owners/ResourceApproversList'

const smallScreenWidth = 960

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: 'relative',
            padding: theme.spacing(2),
            display: 'flex',
            miHeight: '156px',
            justifyContent: 'space-between',

            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                flexDirection: 'column',
            },
        },
        azureApplicationDetailsLabel: {
            color: '#1b1f1f',
            textDecoration: 'underline',
        },

        azureApplicationDetailsIdValues: {
            color: theme?.palette?.primary?.main,
        },

        content: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            [`@media (max-width:345px)`]: {
                flexDirection: 'column-reverse',
            },
            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                display: 'block',
            },
        },
    }),
)

const hasAccessToResponsiblePartyControlName =
    'ResourceAdmin-GroupDetails-ResponsibleParty'
const hasAccessToApproversControlName = 'ResourceAdmin-GroupDetails-Approvers'

const canSeeOwnersControlName = 'ResourceAdmin-GroupsDetails-Owners-Control'

export const MainOverview = (props) => {
    const { basicAttributes, groupId } = props

    const classes = useStyles()
    const { t } = useTranslation()

    const { hasAccessToControl } = useSubcomponents()

    const { isLoading: detailsLoading, data: group } = useGroup(groupId)
    const policy = useAccessRequestPolicy(group && group.requestPolicyId)

    const groupDetails = (
        <Box>
            {basicAttributes &&
                basicAttributes.map((att, i) => {
                    return (
                        <Attribute key={att.value + i}>
                            <AttributeLabel>{t(att.label)}</AttributeLabel>
                            <AttributeValue>
                                {att.render
                                    ? att.render(group)
                                    : group && group[att.value]}
                            </AttributeValue>
                        </Attribute>
                    )
                })}
        </Box>
    )
    const policyDetails = (
        <Box>
            <Attribute>
                <AttributeLabel>{t('PublishInIAMShop')}</AttributeLabel>
                <AttributeValue>
                    {group?.publishInIamShop ? t('Yes') : t('No')}
                </AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>{t('AccessRequestPolicy')}</AttributeLabel>
                <AttributeValue>{policy && policy.name}</AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>
                    {t('AllowsTimeConstrainedAccess')}
                </AttributeLabel>
                <AttributeValue>
                    {policy && policy.isTimeConstrained ? t('Yes') : t('No')}
                </AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>{t('DefaultAccessDuration')}</AttributeLabel>
                <AttributeValue>
                    {policy && policy.defaultValueInMinutes}
                </AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>{t('MaxAccessDuration')}</AttributeLabel>
                <AttributeValue>
                    {policy && policy.maximumValueInMinutes}
                </AttributeValue>
            </Attribute>
        </Box>
    )

    const renderContent = () => (
        <Fragment>
            <Box className={classes.content}>
                <Box>{groupDetails}</Box>

                <Box>{policyDetails}</Box>

                <Box>
                    {hasAccessToControl(canSeeOwnersControlName) && (
                        <Box
                            data-protectedsubcomponent={canSeeOwnersControlName}
                        >
                            <ResourceOwnersList
                                resourceId={groupId}
                                label="Groups_Owners"
                                contextKey="RequestViewGroupsOwners"
                            />
                        </Box>
                    )}
                    {hasAccessToControl(hasAccessToApproversControlName) && (
                        <Box
                            data-protectedsubcomponent={
                                hasAccessToApproversControlName
                            }
                        >
                            <ResourceApproversList
                                resourceId={groupId}
                                label="Approvers"
                                contextKey="RequestViewGroupsApprovers"
                            />
                        </Box>
                    )}
                    {hasAccessToControl(
                        hasAccessToResponsiblePartyControlName,
                    ) && (
                        <Box
                            data-protectedsubcomponent={
                                hasAccessToResponsiblePartyControlName
                            }
                        >
                            {group?.ownerIsPerson ? (
                                <ResponsibleParty
                                    responsiblePersonId={group?.ownerId}
                                />
                            ) : (
                                <Attribute>
                                    <AttributeLabel>
                                        {t('ResponsibleParty')}
                                    </AttributeLabel>
                                    <AttributeValue>
                                        {group?.ownerFriendlyName || '-'}
                                    </AttributeValue>
                                </Attribute>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Fragment>
    )
    return (
        <>
            {detailsLoading ? (
                <Box width={'100%'}>
                    <Skeleton height={40} />
                </Box>
            ) : (
                <Fragment>
                    <Box>{renderContent()}</Box>
                </Fragment>
            )}
        </>
    )
}
